import * as React from 'react'

import compact from 'lodash/compact'
import flattenDeep from 'lodash/flattenDeep'
import intersection from 'lodash/intersection'
import isEmpty from 'lodash/isEmpty'
import keyBy from 'lodash/keyBy'
import keys from 'lodash/keys'
import omit from 'lodash/omit'
import orderBy from 'lodash/orderBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import {ConnectedProps} from 'react-redux'

import config from '../../config'

import * as navigationActions from '../../actions/navigation'
import * as ordersActions from '../../actions/order'
import {State} from '../../reducers'
import {getInitialStatusFilter, initialCreationDateRange} from '../../reducers/order'

import useSearch from '../../../../commons/src/hooks/use-search'
import {isBefore, isBeforeDay, isTodaysDate} from '../../../../commons/src/services/date'
import {captureIgnoredException} from '../../../../commons/src/services/logging'
import {connect} from '../../../../commons/src/services/loop'
import {useExpandedRows} from '../../hooks/use-expanded-rows'
import useSortAttributes, {SortDefinition} from '../../hooks/use-sort-attributes'
import useTableRowFilters, {FilterMap} from '../../hooks/use-table-row-filters'
import useWindowScrollYPosition from '../../hooks/use-window-scroll-position'
import getCloudinaryUrl from '../../libs/cloudinary'
import {formatDate, formatPrice} from '../../libs/formatter'
import {
  countUnreadMessages,
  getOrderOnlyTags,
  getOrderTags,
  getQuoteTags,
  statusIsClosedOrShipped,
} from '../../libs/order'
import {
  BooleanMatchingFilter,
  StringMatchingFilter,
  ToggleMatchingFilter,
  filterTableRows,
} from '../../libs/table-row-filter'
import {createOrderSearch} from '../../services/search'

import {DateRange} from '../../../../commons/src/types'
import {Color, MaterialConfig} from '../../types'

import reviewIcon from '../../../../commons/src/assets/icons/annotation-review.svg'
import emailIcon from '../../../../commons/src/assets/icons/email.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import CounterTag from '../../../../commons/src/components/counter-tag'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Link from '../../../../commons/src/components/link'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator/loading-indicator'
import MaterialImage from '../../../../commons/src/components/material-image'
import Paragraph from '../../../../commons/src/components/paragraph'
import SearchField from '../../../../commons/src/components/search-field'
import Stack from '../../../../commons/src/components/stack'
import TruncatedLabel from '../../../../commons/src/components/truncated-label'
import ActionHeaderLayout from '../../components/action-header-layout'
import Container from '../../components/container'
import ModelImage from '../../components/model-image'
import {orderStatusTypeToContentMap} from '../../components/order-status-badge/OrderStatusBadge'
import OrderTag from '../../components/order-tag'
import {typeToTagContentMap} from '../../components/order-tag/OrderTag'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'
import Table from '../../components/table'
import ExpandableTableRow from '../../components/table/ExpandableTableRow'
import TableCell from '../../components/table/TableCell'
import TableCellGroup from '../../components/table/TableCellGroup'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableHeaderCellGroup from '../../components/table/TableHeaderCellGroup'
import TableRow from '../../components/table/TableRow'
import TableRowToggleButton from '../../components/table/TableRowToggleButton'

import {
  Order,
  OrderDetailTab,
  OrderNumber,
  OrderRowContent,
  OrderStatusType,
  QuoteRowContent,
} from '../../order-types'
import BooleanMatchingFilterPartial from '../filters/boolean-matching-filter-partial'
import DateRangeFilterPartial from '../filters/date-range-filter-partial'
import StringMatchingFilterPartial from '../filters/string-matching-filter-partial'
import ToggleMatchingFilterPartial from '../filters/toggle-matching-filter-partial'
import PagePartial from '../page-partial'

import OrderStatusPartial from './order-status-partial'

const mapStateToProps = (state: State) => ({
  printingMethods: state.core.printingMethods,
  materialConfigs: state.core.materialConfigs,
  finishGroups: state.core.finishGroups,
  colors: state.core.colors,
  materials: state.core.materials,
  orders: state.order.orders,
  isLoadingOrders: state.order.isLoading,
  ordersFailed: state.order.failed,
  statusFilter: state.order.statusFilter,
  creationDateRange: state.order.creationDateRange,
})

const mapDispatchToProps = {
  goToOrderDetail: navigationActions.goToOrderDetail,
  loadOrders: ordersActions.loadOrders,
  updateOrder: ordersActions.updateOrder,
  updateStatusFilter: ordersActions.updateStatusFilter,
  updateCreationDateRange: ordersActions.updateCreationDateRange,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const initialFilters: FilterMap<OrderRowContent> = {
  material: new StringMatchingFilter({
    key: 'materialIds',
    filterValues: [],
  }),
  printingMethod: new StringMatchingFilter({
    key: 'printingMethodIds',
    filterValues: [],
  }),
  tags: new StringMatchingFilter({
    key: 'tags',
    filterValues: [],
  }),
  reviews: new BooleanMatchingFilter({
    key: 'hasReview',
    filterValue: undefined,
  }),
  messages: new ToggleMatchingFilter({
    key: 'hasUnreadMessages',
    filterValue: false,
  }),
}

const orderStatusFilterOptions = keys(
  omit(orderStatusTypeToContentMap, [OrderStatusType.UNKNOWN])
).map(key => ({
  id: key,
  label: orderStatusTypeToContentMap[key as OrderStatusType].label,
  searchable: orderStatusTypeToContentMap[key as OrderStatusType].label,
}))

const tagsFilterOptions = Object.keys(
  config.refund.enabled ? typeToTagContentMap : omit(typeToTagContentMap, 'hasRefundRequest')
).map(key => ({
  id: key,
  label: (
    <ActionBar space="70" key={key}>
      <OrderTag type={key} />
      {typeToTagContentMap[key].tooltipContent}
    </ActionBar>
  ),
  searchable: key,
}))

const reviewsFilterOptions = {
  undefined: 'Show all',
  true: 'Show only orders with reviews',
  false: 'Hide orders with reviews',
}

const tableColumnWidths = {
  toggle: '20px',
  group: {
    total: 6.5,
    orderNumber: '115px',
    customerName: 2,
    status: '150px',
    models: 0.7,
  },
  quantity: 0.7,
  materials: 0.7,
  methods: 0.7,
  tags: 1,
  production: 1,
  shipping: 1,
  additionalCost: 1,
  shippingDueDate: 1,
  placementDate: 1,
  details: 1,
  messages: 0.5,
}

const quickAccessValues = [
  {label: 'today', value: 'today', days: 0},
  {label: 'last 30 days', value: 'last30days', days: 30},
  {label: 'last 7 days', value: 'last7days', days: 7},
  {label: 'last 90 days', value: 'last90days', days: 90},
]

const SORT_ATTRIBUTES: SortDefinition<OrderRowContent> = {
  customerName: {
    asc: ({customerName: a}, {customerName: b}) => a.localeCompare(b),
    desc: ({customerName: a}, {customerName: b}) => b.localeCompare(a),
  },
  shippingDueDate: {
    asc: ({shippingDueDate: a}, {shippingDueDate: b}) => (isBefore(a, b) ? 1 : -1),
    desc: ({shippingDueDate: a}, {shippingDueDate: b}) => (isBefore(a, b) ? -1 : 1),
  },
  placementDate: {
    asc: ({placementDate: a}, {placementDate: b}) => (isBefore(a, b) ? 1 : -1),
    desc: ({placementDate: a}, {placementDate: b}) => (isBefore(a, b) ? -1 : 1),
  },
  productionPrice: {
    asc: ({productionPrice: a}, {productionPrice: b}) => a - b,
    desc: ({productionPrice: a}, {productionPrice: b}) => b - a,
  },
  additionalCost: {
    asc: ({additionalCost: a}, {additionalCost: b}) => a - b,
    desc: ({additionalCost: a}, {additionalCost: b}) => b - a,
  },
  shippingPrice: {
    asc: ({shippingPrice: a}, {shippingPrice: b}) => a - b,
    desc: ({shippingPrice: a}, {shippingPrice: b}) => b - a,
  },
  quantity: {
    asc: ({quantity: a}, {quantity: b}) => a - b,
    desc: ({quantity: a}, {quantity: b}) => b - a,
  },
  modelsCount: {
    asc: ({modelsCount: a}, {modelsCount: b}) => a - b,
    desc: ({modelsCount: a}, {modelsCount: b}) => b - a,
  },
  status: {
    asc: ({status: a}, {status: b}) => a.localeCompare(b),
    desc: ({status: a}, {status: b}) => b.localeCompare(a),
  },
}

const OrdersPage = ({
  orders,
  isLoadingOrders,
  ordersFailed,
  statusFilter,
  creationDateRange,
  printingMethods,
  materialConfigs,
  finishGroups,
  colors,
  materials,
  goToOrderDetail,
  loadOrders,
  updateOrder,
  updateStatusFilter,
  updateCreationDateRange,
}: Props) => {
  const {filters, hasActiveFilters, setFilter, resetFilters} = useTableRowFilters<OrderRowContent>(
    initialFilters,
    ['material', 'printingMethod', 'tags', 'reviews', 'messages']
  )

  useWindowScrollYPosition('orders_scrollY', !isLoadingOrders && !isEmpty(orders))

  const getUniqMaterialIds = (order: Order, useFallback = false) =>
    compact(
      uniq(
        order.quotes.map(q => {
          const materialConfigId = q.materialConfigId
          const materialConfig = materialConfigs[materialConfigId]
          if (materialConfig) {
            const finishGroup = finishGroups[materialConfig.finishGroupId]
            return finishGroup.materialId
          }
          if (useFallback) {
            return q.materialName
          }
          return null
        })
      )
    )

  const getUniqPrintingMethodIds = (order: Order) => {
    const quotes = uniqBy(order.quotes, 'printingMethodId')
    return compact(quotes.map(quote => quote.printingMethodId || undefined))
  }

  const [materialFilterOptions, printingMethodFilterOptions] = React.useMemo(() => {
    if (isEmpty(orders)) {
      return [[], []]
    }
    return [
      uniq(flattenDeep(Object.values(orders).map(order => getUniqMaterialIds(order)))).map(
        materialId => {
          const material = materials[materialId]
          return {
            id: materialId,
            label: material.name,
            searchable: material.name,
          }
        }
      ),
      uniq(flattenDeep(Object.values(orders).map(order => getUniqPrintingMethodIds(order)))).map(
        printingMethodId => {
          const printingMethod = printingMethods[printingMethodId]
          const printingMethodName = printingMethod?.name ?? printingMethodId // fallback if printing method does not exist in the catalog anymore

          return {
            id: printingMethodId,
            label: printingMethodName,
            searchable: printingMethodName,
          }
        }
      ),
    ]
  }, [orders])

  const [tableRows, quoteTableRows] = React.useMemo(() => {
    if (isLoadingOrders || isEmpty(orders)) {
      return [[], {}]
    }

    const orderTableRows: OrderRowContent[] = Object.values(orders).map((order: Order) => {
      const quantitySum = order.quotes.reduce((acc: number, quote) => acc + quote.quantity, 0)
      const materialIds = getUniqMaterialIds(order, true)
      const printingMethodIds = getUniqPrintingMethodIds(order)

      const placementDate =
        order.history.find(historyItem => historyItem.status === OrderStatusType.ORDERED)?.date ||
        ''

      if (placementDate === '') {
        captureIgnoredException(
          new Error(`No "ordered" history item found for order: #${order.orderNumber}`)
        )
      }

      const productionPrice = order.amounts.cartItems?.netPrice || 0
      const shippingPrice = order.amounts.shipping?.netPrice || 0
      const additionalCost = order.amounts.additionalCosts?.netPrice || 0
      const currency =
        order.amounts.cartItems?.currency ||
        order.amounts.shipping?.currency ||
        order.amounts.additionalCosts?.currency ||
        ''
      const unreadMessagesCount = countUnreadMessages(order)

      return {
        orderNumber: order.orderNumber,
        orderId: order.orderId,
        customerName: `${order.customer.firstName} ${order.customer.lastName}`,
        status: order.printingService.status,
        modelsCount: order.models.length,
        quantity: quantitySum,
        materialIds,
        printingMethodIds,
        tags: getOrderTags(order),
        productionPrice,
        formattedProductionPrice: formatPrice(productionPrice, currency, 2),
        shippingPrice,
        formattedShippingPrice: formatPrice(shippingPrice, currency, 2),
        additionalCost,
        formattedAdditionalCost: additionalCost > 0 ? formatPrice(additionalCost, currency, 2) : '',
        shippingDueDate: order.shippingDueDate,
        placementDate,
        messagesCount: order.messages?.length || 0,
        unreadMessagesCount,
        hasUnreadMessages: unreadMessagesCount > 0,
        hasReview: order.hasReview,
      }
    })

    const sortedOrderTableRows = orderBy(orderTableRows, 'placementDate', 'desc')

    const quoteByOrderTableRows: Record<OrderNumber, QuoteRowContent[]> = {}
    Object.values(orders).forEach((order: Order) => {
      const modelsById = keyBy(order.models, 'modelId')

      quoteByOrderTableRows[order.orderNumber] = order.quotes.map(quote => {
        const model = modelsById[quote.modelId]

        // MaterialConfig may not exist anymore in the material catalogue
        const materialConfig: MaterialConfig | undefined = materialConfigs[quote.materialConfigId]
        const colorId = materialConfig?.colorId
        // Color may not exist anymore in the material catalogue
        const color: Color | undefined = colors[colorId]

        return {
          quoteId: quote.quoteId,
          modelThumbnailUrl: model.thumbnailUrl,
          fileName: model.fileName,
          quantity: quote.quantity,
          materialName: quote.vendorMaterialName || quote.materialName,
          colorHexCode: color?.hexCode,
          colorImage: color?.image,
          printingMethodName: quote.printingMethodId
            ? printingMethods[quote.printingMethodId]?.name
            : '',
          tags: getQuoteTags(quote),
          productionPrice: formatPrice(quote.amounts.netPrice, quote.amounts.currency, 2),
        }
      })
    })

    return [sortedOrderTableRows, quoteByOrderTableRows]
  }, [orders])

  const {searchResults, setSearchValue, searchValue} = useSearch(tableRows, createOrderSearch)

  const filteredTableRows = React.useMemo(
    () => filterTableRows(tableRows, Object.values(filters)),
    [filters, tableRows]
  )

  const orderTableRows = React.useMemo(
    () => intersection(searchResults, filteredTableRows),
    [searchResults, filteredTableRows]
  )

  const {
    sortedItems: sortedOrderTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(orderTableRows, SORT_ATTRIBUTES, 'placementDate')

  const {isExpanded, toggleExpansion, setAllExpanded, isAnyExpanded} = useExpandedRows<OrderNumber>(
    config.localStorageKeys.orderExpandedIds,
    () => sortedOrderTableRows.map(t => t.orderNumber)
  )

  const renderOrderQuoteRow = (quoteRow: QuoteRowContent) => (
    <TableRow key={quoteRow.quoteId}>
      <TableCell />
      <TableCellGroup>
        <TableCell>
          <ActionBar space="80" noWrap>
            <ModelImage
              source={quoteRow.modelThumbnailUrl}
              size="s"
              alt={`Preview image of model ${quoteRow.fileName}`}
            />
            <TruncatedLabel>{quoteRow.fileName}</TruncatedLabel>
          </ActionBar>
        </TableCell>
      </TableCellGroup>
      <TableCell>
        <CounterTag>{quoteRow.quantity}</CounterTag>
      </TableCell>
      <TableCell>
        <Paragraph classNames={['u-no-margin-bottom']} size="xs">
          {quoteRow.materialName}{' '}
          {(quoteRow.colorHexCode || quoteRow.colorImage) && (
            <MaterialImage
              classNames={['u-margin-left-xs']}
              size="xs"
              square
              border
              image={
                quoteRow.colorImage &&
                getCloudinaryUrl(quoteRow.colorImage, ['w_700', 'h_458', 'c_fill'])
              }
              color={quoteRow.colorHexCode}
            />
          )}
        </Paragraph>
      </TableCell>
      <TableCell>
        <TruncatedLabel>{quoteRow.printingMethodName}</TruncatedLabel>
      </TableCell>
      <TableCell>
        <ActionBar space="90">
          {quoteRow.tags.map(tag => (
            <OrderTag key={`${quoteRow.quoteId}_${tag}`} type={tag} />
          ))}
        </ActionBar>
      </TableCell>
      <TableCell align="right">{quoteRow.productionPrice}</TableCell>
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
      <TableCell />
    </TableRow>
  )

  const renderOrderRow = (row: OrderRowContent) => (
    <ExpandableTableRow
      key={row.orderNumber}
      expanded={isExpanded(row.orderNumber)}
      toggle={() => toggleExpansion(row.orderNumber)}
      subRows={quoteTableRows[row.orderNumber].map(quoteRow => renderOrderQuoteRow(quoteRow))}
      onClick={() => goToOrderDetail(row.orderId, OrderDetailTab.DETAILS, orders[row.orderId])}
    >
      {({expanded, expansionToggle}) => (
        <>
          <TableCell width={tableColumnWidths.toggle} align="left">
            {row.modelsCount > 0 && <ActionBar>{expansionToggle}</ActionBar>}
          </TableCell>
          <TableCellGroup>
            <TableCell width={tableColumnWidths.group.orderNumber} strong>
              <ActionBar space="80">
                <Link
                  onClick={event => {
                    event.preventDefault()
                    goToOrderDetail(row.orderId, OrderDetailTab.DETAILS, orders[row.orderId])
                  }}
                >
                  {row.orderNumber}
                </Link>
                {row.hasReview && (
                  <Link
                    appearance="noUnderline"
                    onClick={event => {
                      event.preventDefault()
                      goToOrderDetail(row.orderId, OrderDetailTab.REVIEW, orders[row.orderId])
                    }}
                  >
                    <Icon source={reviewIcon} />
                  </Link>
                )}
              </ActionBar>
            </TableCell>
            <TableCell width={tableColumnWidths.group.customerName}>
              <span>{row.customerName}</span>
            </TableCell>
            <TableCell width={tableColumnWidths.group.status}>
              <OrderStatusPartial
                size="90"
                order={orders[row.orderId]}
                onOrderUpdate={(updatedOrder: Order) => {
                  updateOrder(updatedOrder)
                }}
              />
            </TableCell>
            <TableCell width={tableColumnWidths.group.models}>
              {row.modelsCount > 0 && <CounterTag>{row.modelsCount}</CounterTag>}
            </TableCell>
          </TableCellGroup>
          <TableCell>
            {row.quantity > 0 && <CounterTag disabled={expanded}>{row.quantity}</CounterTag>}
          </TableCell>
          <TableCell>
            {row.materialIds.length > 0 && (
              <CounterTag disabled={expanded}>{row.materialIds.length}</CounterTag>
            )}
          </TableCell>
          <TableCell>
            {row.printingMethodIds.length > 0 && (
              <CounterTag disabled={expanded}>{row.printingMethodIds.length}</CounterTag>
            )}
          </TableCell>
          <TableCell>
            <ActionBar space="90">
              {expanded
                ? getOrderOnlyTags(row.tags).map(tag => (
                    <OrderTag key={`${row.orderNumber}_${tag}`} type={tag} />
                  ))
                : row.tags.map(tag => <OrderTag key={`${row.orderNumber}_${tag}`} type={tag} />)}
            </ActionBar>
          </TableCell>
          <TableCell align="right" minor={expanded}>
            <span>{row.formattedProductionPrice}</span>
          </TableCell>
          <TableCell align="right">
            <span>{row.formattedShippingPrice}</span>
          </TableCell>
          <TableCell align="right">{row.formattedAdditionalCost}</TableCell>
          <TableCell
            align="right"
            warning={!statusIsClosedOrShipped(row.status) && isTodaysDate(row.shippingDueDate)}
            error={
              !statusIsClosedOrShipped(row.status) && isBeforeDay(row.shippingDueDate, new Date())
            }
          >
            <span>{row.shippingDueDate ? formatDate(row.shippingDueDate) : '-'}</span>
          </TableCell>
          <TableCell align="right">
            <span>{row.placementDate ? formatDate(row.placementDate) : '-'}</span>
          </TableCell>
          <TableCell align="center">
            {row.messagesCount > 0 && (
              <LinkButton
                icon={<Icon source={emailIcon} />}
                onClick={event => {
                  event.preventDefault()
                  goToOrderDetail(row.orderId, OrderDetailTab.MESSAGES, orders[row.orderId])
                }}
              >
                {row.unreadMessagesCount > 0 && (
                  <CounterTag appearance="alternative">{row.unreadMessagesCount}</CounterTag>
                )}
              </LinkButton>
            )}
          </TableCell>
        </>
      )}
    </ExpandableTableRow>
  )

  const getTableEmpty = () => {
    if (ordersFailed) {
      return (
        <>
          <Paragraph error>
            We are currently unable to load any of your orders. Please wait a few minutes and then
            try again.
          </Paragraph>
          <Button hierarchy="secondary" onClick={loadOrders}>
            Try again
          </Button>
        </>
      )
    }
    if (!isLoadingOrders && isEmpty(orders) && !statusFilter.isActive()) {
      return 'No orders yet'
    }
    return 'No orders found for the specified filters.'
  }

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Orders" />}
            actions={
              <Button
                hierarchy="secondary"
                onClick={() => loadOrders()}
                icon={isLoadingOrders ? <LoadingIndicator /> : <Icon source={refreshIcon} />}
                disabled={isLoadingOrders}
              >
                Refresh
              </Button>
            }
          />
        }
      >
        <Container>
          <ActionHeaderLayout
            filterBar={
              <Stack>
                <FilterBar
                  filtersLabel="Retrieve orders for"
                  filters={
                    <>
                      <DateRangeFilterPartial
                        label={`${formatDate(creationDateRange.from)} -> ${formatDate(
                          creationDateRange.to
                        )}`}
                        range={creationDateRange}
                        apply={(newRange: DateRange) => {
                          updateCreationDateRange(newRange)
                          setAllExpanded(false)
                        }}
                        onReset={() => {
                          updateCreationDateRange(initialCreationDateRange)
                          setAllExpanded(false)
                        }}
                        inputLabel="Placement date"
                        quickAccessValues={quickAccessValues}
                      />
                      <StringMatchingFilterPartial
                        label="Status"
                        options={orderStatusFilterOptions}
                        filterKey="status"
                        filterMap={{status: statusFilter}}
                        tableRows={tableRows}
                        applyFilter={newFilter => {
                          updateStatusFilter(newFilter)
                          setAllExpanded(false)
                        }}
                        clearLabel="Reset"
                        showCount={false}
                        onClear={() => {
                          // reset instead of clear
                          updateStatusFilter(getInitialStatusFilter())
                          setAllExpanded(false)
                        }}
                      />
                    </>
                  }
                />
                <FilterBar
                  filtersLabel="Filter orders by"
                  filters={
                    <>
                      <StringMatchingFilterPartial
                        label="Material"
                        size="90"
                        options={materialFilterOptions}
                        filterKey="material"
                        filterMap={filters}
                        tableRows={searchResults}
                        applyFilter={newFilter =>
                          setFilter('material', newFilter, newFilter.serialize().filterValues)
                        }
                        showSearch
                      />
                      <StringMatchingFilterPartial
                        label="Printing Method"
                        size="90"
                        options={printingMethodFilterOptions}
                        filterKey="printingMethod"
                        filterMap={filters}
                        tableRows={searchResults}
                        applyFilter={newFilter => {
                          setFilter('printingMethod', newFilter, newFilter.serialize().filterValues)
                        }}
                        showSearch
                      />
                      <StringMatchingFilterPartial
                        label="Tags"
                        size="90"
                        options={tagsFilterOptions}
                        filterKey="tags"
                        filterMap={filters}
                        tableRows={searchResults}
                        applyFilter={newFilter =>
                          setFilter('tags', newFilter, newFilter.serialize().filterValues)
                        }
                      />
                      <BooleanMatchingFilterPartial
                        label="Reviews"
                        size="90"
                        options={reviewsFilterOptions}
                        filterKey="reviews"
                        filterMap={filters}
                        tableRows={searchResults}
                        applyFilter={newFilter =>
                          setFilter('reviews', newFilter, newFilter.serialize().filterValue)
                        }
                      />
                      <ToggleMatchingFilterPartial
                        label="only with unread messages"
                        filterKey="messages"
                        filterMap={filters}
                        tableRows={searchResults}
                        applyFilter={newFilter =>
                          setFilter('messages', newFilter, newFilter.serialize().filterValue)
                        }
                      />
                    </>
                  }
                  clearAllLabel="Clear all"
                  onClearAll={hasActiveFilters() ? resetFilters : undefined}
                  searchBar={
                    <SearchField
                      tiny
                      name="order-search"
                      placeholder="Search by name or order number"
                      value={searchValue}
                      onChange={value => {
                        setSearchValue(value)
                      }}
                      onClearClick={() => {
                        setSearchValue('')
                      }}
                    />
                  }
                />
              </Stack>
            }
          >
            <Table
              isLoading={isLoadingOrders}
              empty={getTableEmpty()}
              header={
                <TableRow>
                  <TableHeaderCell width={tableColumnWidths.toggle} align="left">
                    <TableRowToggleButton
                      expanded={isAnyExpanded()}
                      onClick={() => {
                        setAllExpanded(!isAnyExpanded())
                      }}
                    />
                  </TableHeaderCell>
                  <TableHeaderCellGroup width={tableColumnWidths.group.total}>
                    <TableHeaderCell width={tableColumnWidths.group.orderNumber}>
                      Order number
                    </TableHeaderCell>
                    <TableHeaderCell
                      width={tableColumnWidths.group.customerName}
                      sort={sortAttribute === 'customerName' && sortOrder ? sortOrder : undefined}
                      onClick={() => toggleSortBy('customerName')}
                    >
                      Customer name
                    </TableHeaderCell>
                    <TableHeaderCell
                      width={tableColumnWidths.group.status}
                      sort={sortAttribute === 'status' && sortOrder ? sortOrder : undefined}
                      onClick={() => toggleSortBy('status')}
                    >
                      Status
                    </TableHeaderCell>
                    <TableHeaderCell
                      width={tableColumnWidths.group.models}
                      sort={sortAttribute === 'modelsCount' && sortOrder ? sortOrder : undefined}
                      onClick={() => toggleSortBy('modelsCount')}
                    >
                      Models
                    </TableHeaderCell>
                  </TableHeaderCellGroup>
                  <TableHeaderCell
                    width={tableColumnWidths.quantity}
                    sort={sortAttribute === 'quantity' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('quantity')}
                  >
                    Quantity
                  </TableHeaderCell>
                  <TableHeaderCell width={tableColumnWidths.materials}>Materials</TableHeaderCell>
                  <TableHeaderCell width={tableColumnWidths.methods}>Methods</TableHeaderCell>
                  <TableHeaderCell width={tableColumnWidths.tags}>Tags</TableHeaderCell>
                  <TableHeaderCell
                    width={tableColumnWidths.production}
                    align="right"
                    sort={sortAttribute === 'productionPrice' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('productionPrice')}
                  >
                    Production
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={tableColumnWidths.shipping}
                    align="right"
                    sort={sortAttribute === 'shippingPrice' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('shippingPrice')}
                  >
                    Shipping
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={tableColumnWidths.additionalCost}
                    align="right"
                    sort={sortAttribute === 'additionalCost' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('additionalCost')}
                  >
                    Additional Cost
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={tableColumnWidths.shippingDueDate}
                    align="right"
                    sort={sortAttribute === 'shippingDueDate' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('shippingDueDate')}
                  >
                    Ship by
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={tableColumnWidths.placementDate}
                    align="right"
                    sort={sortAttribute === 'placementDate' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('placementDate')}
                  >
                    Placement date
                  </TableHeaderCell>
                  <TableHeaderCell width={tableColumnWidths.messages} />
                </TableRow>
              }
            >
              {sortedOrderTableRows.map(renderOrderRow)}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default connector(OrdersPage)
